import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Paper,
  Popover,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DropResult } from "react-beautiful-dnd";
import { DraggableList } from "../../components/draggable-list/DraggableList";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Colors } from "../../Colors";
import { RootState } from "../../store";
import { AuthorizationSource, InsuranceType } from "../../types";
import SnackBar from "../../subcomponents/SnackBar";
import { bodyCellSx, sectionCardSx, tableHeadCellSx } from "./payersStyles";

interface EditableNumberCellProps {
  value: number;
  onSave: (newValue: number) => void;
}

const EditableNumberCell: React.FC<EditableNumberCellProps> = ({ value, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState(value.toString());

  useEffect(() => {
    setDraftValue(value.toString());
  }, [value]);

  const handleCancel = () => {
    setDraftValue(value.toString());
    setIsEditing(false);
  };

  const handleConfirm = () => {
    const parsed = parseInt(draftValue, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      onSave(parsed);
    }
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <TextField
          type="number"
          size="small"
          autoFocus
          value={draftValue}
          onChange={(e) => setDraftValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleConfirm();
            if (e.key === "Escape") handleCancel();
          }}
          inputProps={{ min: 0 }}
          sx={{
            width: 80,
            "& .MuiOutlinedInput-root": {
              backgroundColor: "rgba(255, 255, 255, 0.04)",
              color: "#FFF",
              fontSize: "0.813rem",
              "& fieldset": { borderColor: "rgba(255, 255, 255, 0.12)" },
              "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
              "&.Mui-focused fieldset": { borderColor: Colors.primary },
            },
          }}
        />
        <IconButton onClick={handleConfirm} size="small" sx={{ color: Colors.success }}>
          <CheckIcon fontSize="small" />
        </IconButton>
        <IconButton onClick={handleCancel} size="small" sx={{ color: Colors.info }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        "&:hover .edit-btn": { opacity: 1 },
      }}
    >
      <Typography sx={{ fontSize: "0.813rem", color: "#FFF", minWidth: "32px" }}>{value}</Typography>
      <IconButton
        className="edit-btn"
        onClick={() => setIsEditing(true)}
        size="small"
        sx={{
          opacity: 0,
          transition: "opacity 0.15s",
          color: Colors.info,
          "&:hover": { color: Colors.primary },
        }}
      >
        <EditIcon sx={{ fontSize: "14px" }} />
      </IconButton>
    </Box>
  );
};

const AUTH_SOURCE_OPTIONS: AuthorizationSource[] = ["prescription", "authorization", "policy"];
const DEFAULT_AUTH_SOURCE_PRIORITY: AuthorizationSource[] = ["prescription", "authorization", "policy"];

interface AuthorizationSourcePriorityCellProps {
  value: AuthorizationSource[] | undefined;
  onSave: (newValue: AuthorizationSource[]) => void;
}

const AuthorizationSourcePriorityCell: React.FC<AuthorizationSourcePriorityCellProps> = ({ value, onSave }) => {
  const current = value ?? DEFAULT_AUTH_SOURCE_PRIORITY;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<{ source: AuthorizationSource; enabled: boolean }[]>([]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    const activeSet = new Set(current);
    const activeItems = current.map((s) => ({ source: s, enabled: true }));
    const inactiveItems = AUTH_SOURCE_OPTIONS.filter((s) => !activeSet.has(s)).map((s) => ({ source: s, enabled: false }));
    setDraft([...activeItems, ...inactiveItems]);
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSave = () => {
    onSave(draft.filter((d) => d.enabled).map((d) => d.source) as AuthorizationSource[]);
    handleClose();
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const reordered = [...draft];
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setDraft(reordered);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          "&:hover .edit-btn": { opacity: 1 },
        }}
      >
        {current.length === 0 ? (
          <Typography sx={{ fontSize: "0.75rem", color: Colors.info }}>—</Typography>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
            {current.map((s, i) => (
              <>
                <Tooltip key={s} title={i === 0 ? "Highest priority" : i === current.length - 1 ? "Lowest priority" : ""} placement="top">
                  <Chip
                    label={s}
                    size="small"
                    sx={{
                      fontSize: "0.7rem",
                      height: "20px",
                      backgroundColor: i === 0 ? `${Colors.primary}22` : "rgba(255, 255, 255, 0.06)",
                      color: i === 0 ? Colors.primary : "#FFF",
                      border: `1px solid ${i === 0 ? `${Colors.primary}44` : "rgba(255, 255, 255, 0.12)"}`,
                    }}
                  />
                </Tooltip>
                {i < current.length - 1 && (
                  <Typography key={`arrow-${i}`} sx={{ fontSize: "0.6rem", color: Colors.info, lineHeight: 1 }}>
                    ›
                  </Typography>
                )}
              </>
            ))}
          </Box>
        )}
        <IconButton
          className="edit-btn"
          onClick={handleOpen}
          size="small"
          sx={{
            opacity: 0,
            transition: "opacity 0.15s",
            color: Colors.info,
            "&:hover": { color: Colors.primary },
          }}
        >
          <EditIcon sx={{ fontSize: "14px" }} />
        </IconButton>
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            backgroundColor: "#1e1e2e",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            borderRadius: "10px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
            minWidth: 260,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: Colors.info,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              mb: 0.25,
            }}
          >
            Authorization Source Priority
          </Typography>
          <Typography sx={{ fontSize: "0.7rem", color: Colors.info, mb: 1.5 }}>Drag to reorder — top item has highest priority</Typography>
          <DraggableList
            items={draft}
            onDragEnd={handleDragEnd}
            getItemId={(item) => item.source}
            droppableId="authorization-source-priority"
            sx={{ minHeight: "unset" }}
            renderItem={(item) => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 0.75,
                  px: 0.5,
                  borderRadius: "6px",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
                }}
              >
                <DragIndicatorIcon sx={{ fontSize: "16px", color: "rgba(255,255,255,0.3)" }} />
                <Checkbox
                  checked={item.enabled}
                  onChange={() => setDraft((prev) => prev.map((d) => (d.source === item.source ? { ...d, enabled: !d.enabled } : d)))}
                  size="small"
                  sx={{
                    p: 0,
                    color: "rgba(255,255,255,0.3)",
                    "&.Mui-checked": { color: Colors.primary },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "0.813rem",
                    color: item.enabled ? "#FFF" : Colors.info,
                  }}
                >
                  {item.source}
                </Typography>
              </Box>
            )}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 1.5,
            }}
          >
            <Button onClick={handleClose} size="small" sx={{ color: Colors.info, fontSize: "0.75rem" }}>
              Cancel
            </Button>
            <Button onClick={handleSave} size="small" variant="contained" sx={{ fontSize: "0.75rem" }}>
              Save
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

interface InsuranceTypesProps {
  refreshKey: number;
}

export default function InsuranceTypes({ refreshKey }: InsuranceTypesProps) {
  const user = useSelector((state: RootState) => state.auth.user);

  const [payerTypes, setPayerTypes] = useState<InsuranceType[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [typesError, setTypesError] = useState<string | null>(null);
  const [updateMessage, setUpdateMessage] = useState<{ text: string; severity: "success" | "error" } | null>(null);

  const fetchPayerTypes = useCallback(async () => {
    if (!user?.token) return;
    setTypesLoading(true);
    setTypesError(null);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/payers/types`, { headers: { Authorization: `Bearer ${user.token}` } });
      const data = response.data as InsuranceType[];
      setPayerTypes(data.sort((a, b) => parseInt(a.ehrInsuranceTypeId) - parseInt(b.ehrInsuranceTypeId)));
    } catch {
      setTypesError("Failed to load insurance types.");
    } finally {
      setTypesLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    fetchPayerTypes();
  }, [fetchPayerTypes, refreshKey]);

  const updateInsuranceType = async (payerId: string, updatedData: Partial<InsuranceType>) => {
    const previous = payerTypes;
    setPayerTypes((prev) => prev.map((type) => (type.insuranceTypeId === payerId ? { ...type, ...updatedData } : type)));
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/payers/types/${payerId}`, updatedData, { headers: { Authorization: `Bearer ${user?.token}` } });
    } catch {
      setPayerTypes(previous);
      setUpdateMessage({
        text: "Failed to update insurance type.",
        severity: "error",
      });
    }
  };

  return (
    <>
      {updateMessage && <SnackBar message={updateMessage.text} severity={updateMessage.severity} />}
      <Paper sx={sectionCardSx}>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#FFF" }}>Insurance Types</Typography>
            <Typography sx={{ fontSize: "0.75rem", color: Colors.info, mt: 0.25 }}>Set per-hour patient limits and default allowed visits for each insurance type.</Typography>
          </Box>
          {typesError && <Typography sx={{ color: Colors.error, fontSize: "0.75rem" }}>{typesError}</Typography>}
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...tableHeadCellSx, pl: 3, width: "80px" }}>ID</TableCell>
                <TableCell sx={tableHeadCellSx}>Type</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: "180px" }}>Limit Per Hour</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: "200px" }}>Default Allowed Visits</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: "220px" }}>Override Threshold</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, pr: 3, width: "340px" }}>Authorization Source Priority</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {typesLoading && payerTypes.length === 0 ? (
                Array.from({ length: 4 }).map((_, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ ...bodyCellSx, pl: 3 }}>
                      <Skeleton variant="text" sx={{ bgcolor: "rgba(255, 255, 255, 0.08)" }} />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Skeleton variant="text" sx={{ bgcolor: "rgba(255, 255, 255, 0.08)" }} />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Skeleton variant="text" width={60} sx={{ bgcolor: "rgba(255, 255, 255, 0.08)" }} />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Skeleton variant="text" width={60} sx={{ bgcolor: "rgba(255, 255, 255, 0.08)" }} />
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, pr: 3 }}>
                      <Skeleton variant="text" width={160} sx={{ bgcolor: "rgba(255, 255, 255, 0.08)" }} />
                    </TableCell>
                  </TableRow>
                ))
              ) : payerTypes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ ...bodyCellSx, border: 0, py: 5, textAlign: "center" }}>
                    <Typography sx={{ color: Colors.info, fontSize: "0.813rem" }}>No insurance types yet. Sync from your EHR to get started.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                payerTypes.map((type) => (
                  <TableRow
                    key={type.ehrInsuranceTypeId}
                    sx={{
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.02)",
                      },
                    }}
                  >
                    <TableCell sx={{ ...bodyCellSx, pl: 3 }}>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: Colors.info,
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        }}
                      >
                        {type.ehrInsuranceTypeId}
                      </Typography>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>{type.name}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      <EditableNumberCell
                        value={type.limitPerHour ?? 0}
                        onSave={(newValue) =>
                          updateInsuranceType(type.insuranceTypeId, {
                            limitPerHour: newValue,
                          })
                        }
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <EditableNumberCell
                        value={type.defaultAllowedVisits ?? 0}
                        onSave={(newValue) =>
                          updateInsuranceType(type.insuranceTypeId, {
                            defaultAllowedVisits: newValue,
                          })
                        }
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <EditableNumberCell
                        value={type.defaultAllowedVisitsOverrideThreshold ?? 0}
                        onSave={(newValue) =>
                          updateInsuranceType(type.insuranceTypeId, {
                            defaultAllowedVisitsOverrideThreshold: newValue,
                          })
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, pr: 3 }}>
                      <AuthorizationSourcePriorityCell
                        value={type.authorizationSourcePriority}
                        onSave={(newValue) =>
                          updateInsuranceType(type.insuranceTypeId, {
                            authorizationSourcePriority: newValue,
                          })
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
}
